/**
 * This enables the use of multiple mixins that provide static `onEnter` methods to be
 * used with `react-router` views.
 *
 * This is used by providing an array of mixins like so:
 *
 *   static onEnter = requireOnEnter([RequireUser])
 *
 * The order matters. chainOnEnter will skip any remaining onEnters once replaceWith is called.
 *
 * You must specify the onEnter method in the route definition, or it will never get called:
 *
 *   <Route
 *     path="/chat/leave/:token"
 *     component={LeaveChatView}
 *     onEnter={LeaveChatView.onEnter}
 *    />
 */

import { RouterState, LocationDescriptor } from 'lib/ReactRouterTypes';

const ASYNC_ARITY = 3;

export type ChainedFunction = (
  nextState: RouterState,
  replaceWith: (location: LocationDescriptor) => void,
  routerCallback?: () => void
) => void;

export type ChainedFunctionArgs = ArgumentTypes<ChainedFunction>;

// take the arguments of ChainFunction and change the return value
type modifiedNoCbArgs = [ChainedFunctionArgs[0], ChainedFunctionArgs[1]];

export type InputChainedFunction = ((...a: modifiedNoCbArgs) => Promise<void>) | ChainedFunction;

// returns a fn for react-route onEnter static prop.
export default function chainOnEnter(
  onEnterFunctions: Array<InputChainedFunction>
): ChainedFunction {
  return function(
    nextState: RouterState,
    replaceWith: (location: LocationDescriptor) => void,
    routerCallback?: () => void
  ) {
    let i = 0;
    let replaceCalled = false;
    const replaceOnce = (loc: LocationDescriptor) => {
      replaceCalled = true;
      replaceWith(loc);
    };

    const recursiveCb = function() {
      if (i < onEnterFunctions.length && !replaceCalled) {
        const nextOnEnter = onEnterFunctions[i++];
        if (nextOnEnter.length < ASYNC_ARITY) {
          const isPromise = nextOnEnter(nextState, replaceOnce);
          if (isPromise instanceof Promise) {
            isPromise.then(recursiveCb); // purposefully not catching.  Handling things here could lead to unintentional bugs
          } else {
            recursiveCb();
          }
        } else {
          const checkPromise = nextOnEnter(nextState, replaceOnce, recursiveCb);
          if (checkPromise instanceof Promise) {
            console.log('nextOnEnter:', nextOnEnter);
            console.log('checkPromise:', checkPromise);
            throw new Error(
              'chainOnEnter cannot handle both callback and returned promise in functions, choose one'
            );
          }
        }
      } else {
        routerCallback && routerCallback();
      }
    };
    recursiveCb();
  };
}
